import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * iOS-style swipe-from-left-edge to go back.
 * - With no args: navigates to previous route (navigate(-1))
 * - With onBack: calls onBack() instead — use this in drill-down screens
 *   so the swipe exits the drill rather than the whole page.
 */
export function useSwipeBack(onBack?: () => void, edgeThreshold = 30, minSwipeDistance = 60) {
  const navigate = useNavigate();
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const onBackRef = useRef(onBack);

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  useEffect(() => {
    const onTouchStart = (e: TouchEvent) => {
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      touchStartX.current = x <= edgeThreshold ? x : null;
      touchStartY.current = x <= edgeThreshold ? y : null;
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (touchStartX.current === null) return;
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const dx = endX - touchStartX.current;
      const dy = Math.abs(endY - (touchStartY.current ?? 0));

      if (dx >= minSwipeDistance && dy < dx * 0.6) {
        if (typeof onBackRef.current === 'function') {
          onBackRef.current();
        } else {
          navigate(-1);
        }
      }
      touchStartX.current = null;
      touchStartY.current = null;
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [navigate, edgeThreshold, minSwipeDistance]);
}
